/**
 * <ec-slider> — lightweight scroll-snap slider shared by Ecloset sections.
 *
 * Markup:
 *   <ec-slider data-autoplay data-speed="5">
 *     <div class="ec-slider__track" data-ec-track> ...slides... </div>
 *     <button data-ec-prev></button> <button data-ec-next></button>   (optional)
 *     <button data-ec-dot="0"></button> ...                            (optional)
 *   </ec-slider>
 *
 * Autoplay only runs while the track overflows (e.g. mobile), pauses on
 * hover/focus/touch, respects prefers-reduced-motion and follows block
 * selection in the theme editor.
 *
 * Optional:
 *   data-bounded  arrows stop at the ends (no wrap-around) and get `disabled`
 *                 on the first / last position
 *   data-drag     click-and-drag scrolling with a mouse (touch already swipes)
 */
if (!customElements.get('ec-slider')) {
  class EcSlider extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-ec-track]');
      if (!this.track) return;

      this.slides = Array.from(this.track.children).filter((el) => !el.hasAttribute('data-ec-slide-ignore'));
      this.dots = Array.from(this.querySelectorAll('[data-ec-dot]'));
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.speed = (parseFloat(this.dataset.speed) || 5) * 1000;
      this.paused = false;

      this.bounded = this.hasAttribute('data-bounded');
      this.prevButton = this.querySelector('[data-ec-prev]');
      this.nextButton = this.querySelector('[data-ec-next]');
      this.prevButton?.addEventListener('click', () => this.step(-1));
      this.nextButton?.addEventListener('click', () => this.step(1));
      this.dots.forEach((dot) => {
        dot.addEventListener('click', () => this.goTo(Number(dot.dataset.ecDot)));
      });

      this.onScroll = this.onScroll.bind(this);
      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      this.updateDots();
      this.updateArrows();

      if (this.bounded && 'ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => this.updateArrows());
        this.resizeObserver.observe(this.track);
      }

      if (this.hasAttribute('data-drag')) this.enableDrag();

      if (this.hasAttribute('data-autoplay') && !this.reducedMotion) {
        const pause = () => (this.paused = true);
        const resume = () => (this.paused = false);
        this.addEventListener('mouseenter', pause);
        this.addEventListener('mouseleave', resume);
        this.addEventListener('focusin', pause);
        this.addEventListener('focusout', resume);
        this.addEventListener('touchstart', pause, { passive: true });
        this.addEventListener('touchend', () => setTimeout(resume, this.speed), { passive: true });
        this.timer = setInterval(() => {
          if (!this.paused && !document.hidden && this.overflows()) this.step(1);
        }, this.speed);
      }

      this.onBlockSelect = (event) => {
        const index = this.slides.indexOf(event.target);
        if (index === -1) return;
        this.paused = true;
        this.goTo(index, true);
      };
      this.onBlockDeselect = (event) => {
        if (this.slides.includes(event.target)) this.paused = false;
      };
      document.addEventListener('shopify:block:select', this.onBlockSelect);
      document.addEventListener('shopify:block:deselect', this.onBlockDeselect);
    }

    disconnectedCallback() {
      clearInterval(this.timer);
      this.resizeObserver?.disconnect();
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
      document.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
    }

    overflows() {
      return this.track.scrollWidth - this.track.clientWidth > 2;
    }

    maxScroll() {
      return this.track.scrollWidth - this.track.clientWidth;
    }

    currentIndex() {
      const left = this.track.scrollLeft;
      let closest = 0;
      this.slides.forEach((slide, i) => {
        if (Math.abs(slide.offsetLeft - left) < Math.abs(this.slides[closest].offsetLeft - left)) closest = i;
      });
      return closest;
    }

    step(direction) {
      const left = this.track.scrollLeft;
      if (this.bounded) return this.goTo(this.currentIndex() + direction);
      if (direction > 0 && left >= this.maxScroll() - 2) return this.goTo(0);
      if (direction < 0 && left <= 2) return this.goTo(this.slides.length - 1);
      this.goTo(this.currentIndex() + direction);
    }

    goTo(index, instant = false) {
      const slide = this.slides[Math.max(0, Math.min(index, this.slides.length - 1))];
      if (!slide) return;
      this.track.scrollTo({
        left: Math.min(slide.offsetLeft, this.maxScroll()),
        behavior: instant || this.reducedMotion ? 'auto' : 'smooth',
      });
    }

    onScroll() {
      if (this.scrollFrame) return;
      this.scrollFrame = requestAnimationFrame(() => {
        this.scrollFrame = null;
        this.updateDots();
        this.updateArrows();
      });
    }

    updateArrows() {
      if (!this.bounded) return;
      const left = this.track.scrollLeft;
      if (this.prevButton) this.prevButton.disabled = left <= 2;
      if (this.nextButton) this.nextButton.disabled = left >= this.maxScroll() - 2;
    }

    enableDrag() {
      let startX = 0;
      let startScroll = 0;
      let dragging = false;
      let moved = false;

      this.track.addEventListener('pointerdown', (event) => {
        if (event.pointerType !== 'mouse' || event.button !== 0) return;
        dragging = true;
        moved = false;
        startX = event.clientX;
        startScroll = this.track.scrollLeft;
      });

      window.addEventListener('pointermove', (event) => {
        if (!dragging) return;
        const delta = event.clientX - startX;
        if (!moved && Math.abs(delta) < 5) return;
        if (!moved) {
          moved = true;
          this.track.classList.add('is-dragging');
        }
        this.track.scrollLeft = startScroll - delta;
      });

      window.addEventListener('pointerup', () => {
        if (!dragging) return;
        dragging = false;
        if (!moved) return;
        this.track.classList.remove('is-dragging');
        this.goTo(this.currentIndex());
      });

      // a drag must not also follow the link/button it started on
      this.track.addEventListener(
        'click',
        (event) => {
          if (!moved) return;
          event.preventDefault();
          event.stopPropagation();
          moved = false;
        },
        true
      );
      this.track.addEventListener('dragstart', (event) => event.preventDefault());
    }

    updateDots() {
      if (!this.dots.length) return;
      const atEnd = this.track.scrollLeft >= this.maxScroll() - 2 && this.overflows();
      const active = atEnd ? this.slides.length - 1 : this.currentIndex();
      this.dots.forEach((dot, i) => dot.setAttribute('aria-current', i === active ? 'true' : 'false'));
    }
  }

  customElements.define('ec-slider', EcSlider);
}
